import { UploadFailureReason } from '../../../../../../../../backend/src/types/types';
import { UploadedFile, UploadFailure, UploadMetadata } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';

/**
 * Downloads data as a CSV file
 */
export function downloadCsv(headers: string[], rows: string[][], filename: string): void {
  const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Get human-readable label for upload failure reason
 */
export function getRejectedReasonLabel(reason: UploadFailureReason): string {
  switch (reason) {
    case UploadFailureReason.Duplicate:
      return 'Duplicate';
    case UploadFailureReason.UnsupportedFileFormat:
      return 'Unsupported file format';
    case UploadFailureReason.NoFileAvailable:
      return 'No file available';
    case UploadFailureReason.DownloadFailed:
      return 'Download failed';
    case UploadFailureReason.StorageError:
      return 'Storage error';
    default:
      return reason;
  }
}

/**
 * Download upload history report as CSV
 */
export function downloadUploadHistoryReport(uploads: UploadMetadata[], matterId: string): void {
  if (!uploads || uploads.length === 0) return;

  const headers = ['#', 'Upload ID', 'Files Uploaded', 'Failed', 'Created By', 'Upload Date'];
  const rows = uploads.map((upload, index) => [
    (uploads.length - index).toString(),
    upload.id,
    (upload.successfullyUploadedFileCount ?? 0).toString(),
    (upload.uploadFailures?.length ?? 0).toString(),
    upload.createdBy?.email || '',
    upload.createdAt ? formatDate(upload.createdAt, true, true) : '',
  ]);

  downloadCsv(headers, rows, `upload-history-${matterId}.csv`);
}

/**
 * Download successful files report as CSV
 */
export function downloadSuccessfulFilesReport(files: UploadedFile[], uploadId: string): void {
  if (!files || files.length === 0) return;

  const headers = ['Filename', 'Type', 'Size (bytes)', 'Upload Date'];
  const rows = files.map((file) => [
    file.name,
    file.extension?.toUpperCase() || '',
    file.size?.toString() || '',
    file.createdDate ? formatDate(file.createdDate, true, true) : '',
  ]);

  downloadCsv(headers, rows, `successful-uploads-${uploadId}.csv`);
}

/**
 * Download failed files report as CSV
 */
export function downloadFailedFilesReport(failures: UploadFailure[], uploadId: string): void {
  if (!failures || failures.length === 0) return;

  const headers = ['Filename', 'Type', 'Upload Date', 'Rejected Reason', 'Duplicate Of'];
  const rows = failures.map((failure) => [
    failure.filename,
    failure.extension?.toUpperCase() || '',
    failure.createdDate ? formatDate(failure.createdDate, true, true) : '',
    failure.rejectedReason ? getRejectedReasonLabel(failure.rejectedReason) : '',
    failure.duplicateFile?.name || '',
  ]);

  downloadCsv(headers, rows, `failed-uploads-${uploadId}.csv`);
}
